import { computed, Ref, ref, watch } from '@nuxtjs/composition-api';
import { PartsDbVehicleSummary } from '@unified-commerce/gpc-vue-storefront-parts-db';

export interface UseVehicleYearsProps {
  vehicles: Ref<PartsDbVehicleSummary[]>;
}

const useVehicleYears = ({ vehicles }: UseVehicleYearsProps) => {
  const selectedExactYear = ref<string | null>(null);

  const apiExactYear = computed(
    () => vehicles.value.find((v) => v.exactYear)?.exactYear?.toString() ?? null,
  );

  const exactYear = computed(() => {
    if (vehicles.value.length === 0) {
      return null;
    }

    return selectedExactYear.value ?? apiExactYear.value;
  });

  const possibleVehicleYears = computed(() => {
    const validYears = vehicles.value
      .flatMap((result) => [result.yearFrom, result.yearTo])
      .filter((result): result is number => Number.isFinite(result));

    if (!validYears.length) {
      return [];
    }

    const yearFrom = Math.min(...validYears);
    const yearTo = Math.max(...validYears);

    return Array.from({ length: yearTo - yearFrom + 1 }, (_, i) => String(yearFrom + i));
  });

  const updateExactYear = (year: string) => {
    selectedExactYear.value = year;
  };

  const noExactYearFound = computed(() => vehicles.value.length > 0 && apiExactYear.value == null);

  watch(vehicles, () => {
    selectedExactYear.value = null;
  });

  return {
    possibleVehicleYears,
    exactYear,
    noExactYearFound,
    updateExactYear,
  };
};

export default useVehicleYears;
